// src/services/admin/scholarships.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const scholarshipsApi = createApi({
  reducerPath: "scholarshipsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api/admin/scholarships`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Scholarships", "ScholarshipEnums", "ScholarshipDocuments"],
  endpoints: (builder) => ({
    // Get signed preview URL for a document
    getScholarshipDocumentPreview: builder.query({
      query: ({ scholarshipId, documentId }) =>
        `/${scholarshipId}/documents/${documentId}/preview`,
    }),
    // --- Scholarship Documents API ---
    // List documents for a scholarship
    getScholarshipDocuments: builder.query({
      query: (scholarshipId) => `/${scholarshipId}/documents`,
      providesTags: (result, error, scholarshipId) => [
        { type: "Scholarships", id: scholarshipId },
        { type: "ScholarshipDocuments", id: scholarshipId },
      ],
    }),

    // Upload document(s) to a scholarship
    uploadScholarshipDocument: builder.mutation({
      query: ({ scholarshipId, file, name, isPrivate }) => {
        // file: File object, name: string, isPrivate: boolean
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", name);
        formData.append("private", isPrivate ? "1" : "0");
        return {
          url: `/${scholarshipId}/documents`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { scholarshipId }) => [
        { type: "ScholarshipDocuments", id: scholarshipId },
      ],
    }),

    // Update document metadata (name, privacy)
    updateScholarshipDocument: builder.mutation({
      query: ({ scholarshipId, documentId, name, isPrivate }) => ({
        url: `/${scholarshipId}/documents/${documentId}`,
        method: "PUT",
        body: { name, private: isPrivate ? 1 : 0 },
      }),
      invalidatesTags: (result, error, { scholarshipId }) => [
        { type: "ScholarshipDocuments", id: scholarshipId },
      ],
    }),

    // Delete document
    deleteScholarshipDocument: builder.mutation({
      query: ({ scholarshipId, documentId }) => ({
        url: `/${scholarshipId}/documents/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { scholarshipId }) => [
        { type: "ScholarshipDocuments", id: scholarshipId },
      ],
    }),
    getScholarships: builder.query({
      query: ({
        page,
        limit,
        search,
        sortField,
        sortOrder,
        filterPriority,
        filterAwardType,
        filterCityId,
        filterActive,
        filterVerified,
        filterPresentedBy,
        filterSelectedBy,
        filterMultiSchool,
        // possibly filterCreatedStart, filterCreatedEnd
      }) => {
        let queryString = `?page=${page}&limit=${limit}&search=${encodeURIComponent(
          search || "",
        )}`;
        if (sortField)
          queryString += `&sortField=${encodeURIComponent(sortField)}`;
        if (sortOrder)
          queryString += `&sortOrder=${encodeURIComponent(sortOrder)}`;
        if (filterPriority)
          queryString += `&filterPriority=${encodeURIComponent(
            filterPriority,
          )}`;
        if (filterAwardType)
          queryString += `&filterAwardType=${encodeURIComponent(
            filterAwardType,
          )}`;
        if (filterCityId)
          queryString += `&filterCityId=${encodeURIComponent(filterCityId)}`;
        if (filterActive !== undefined)
          queryString += `&filterActive=${encodeURIComponent(filterActive)}`;
        if (filterVerified !== undefined)
          queryString += `&filterVerified=${encodeURIComponent(
            filterVerified,
          )}`;
        if (filterPresentedBy)
          queryString += `&filterPresentedBy=${encodeURIComponent(
            filterPresentedBy,
          )}`;
        if (filterSelectedBy)
          queryString += `&filterSelectedBy=${encodeURIComponent(
            filterSelectedBy,
          )}`;
        if (filterMultiSchool)
          queryString += `&filterMultiSchool=${encodeURIComponent(
            filterMultiSchool,
          )}`;

        return queryString;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Scholarships", id })),
              { type: "Scholarships", id: "PARTIAL-LIST" },
            ]
          : [{ type: "Scholarships", id: "PARTIAL-LIST" }],
    }),
    getScholarshipsAll: builder.query({
      query: ({ city_id, selected_by }) => {
        let url = `/all/${city_id}`;
        if (selected_by) {
          url += `?selected_by=${encodeURIComponent(selected_by)}`;
        }
        return url;
      },
      providesTags: (result, error, { city_id }) => [
        { type: "Scholarships", id: city_id },
      ],
    }),
    getScholarshipById: builder.query({
      query: (arg) => {
        const id = typeof arg === "object" ? arg?.id : arg;
        return `/${id}`;
      },
      providesTags: (result, error, arg) => {
        const id = typeof arg === "object" ? arg?.id : arg;
        return [{ type: "Scholarships", id }];
      },
    }),
    createScholarship: builder.mutation({
      query: (newSch) => ({
        url: "/",
        method: "POST",
        body: newSch,
      }),
      invalidatesTags: [{ type: "Scholarships", id: "PARTIAL-LIST" }],
    }),
    updateScholarship: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `/${id}`,
        method: "PUT",
        body: rest,
      }),
      // Invalidate both the list and the individual scholarship
      invalidatesTags: (result, error, { id }) => [
        { type: "Scholarships", id },
        { type: "Scholarships", id: "PARTIAL-LIST" },
      ],
      // Optionally, on success, update the cache for getScholarshipById
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          const updatedScholarship = updated?.success ? updated.data : updated;
          if (!updatedScholarship) return;
          dispatch(
            scholarshipsApi.util.updateQueryData(
              "getScholarshipById",
              id,
              (draft) => {
                const draftScholarship = draft?.success ? draft.data : draft;
                Object.assign(draftScholarship, updatedScholarship);
              },
            ),
          );
        } catch {}
      },
    }),
    deleteScholarship: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Scholarships", id },
        { type: "Scholarships", id: "PARTIAL-LIST" },
      ],
    }),
    getScholarshipEnums: builder.query({
      query: () => "/enums",
      providesTags: ["ScholarshipEnums"],
    }),
    getScholarshipDataByCity: builder.query({
      query: (cityId) => `scholarship-books/${cityId}`,
      skip: (cityId) => !cityId,
    }),
    setScholarshipPresenter: builder.mutation({
      query: ({ id, presenter_name }) => ({
        url: `/presenter/${id}`,
        method: "PUT",
        body: { presenter_name },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Scholarships", id },
        { type: "Scholarships", id: "PARTIAL-LIST" },
      ],
    }),
    getStandbyPresenters: builder.query({
      query: () => `/standby-presenters`,
      providesTags: ["Scholarships"],
    }),
    setStandbyPresenters: builder.mutation({
      query: (presenters) => ({
        url: `/standby-presenters`,
        method: "PUT",
        body: { presenters },
      }),
      invalidatesTags: ["Scholarships"],
    }),
  }),
});

export const {
  useGetScholarshipDocumentPreviewQuery,
  useGetScholarshipsQuery,
  useGetScholarshipsAllQuery,
  useGetScholarshipByIdQuery,
  useLazyGetScholarshipByIdQuery,
  useSetScholarshipPresenterMutation,
  useSetStandbyPresentersMutation,
  useGetStandbyPresentersQuery,
  useCreateScholarshipMutation,
  useUpdateScholarshipMutation,
  useDeleteScholarshipMutation,
  useGetScholarshipEnumsQuery,
  useGetScholarshipDataByCityQuery,
  useLazyGetScholarshipDataByCityQuery,

  // --- Scholarship Documents API ---
  useGetScholarshipDocumentsQuery,
  useUploadScholarshipDocumentMutation,
  useUpdateScholarshipDocumentMutation,
  useDeleteScholarshipDocumentMutation,
} = scholarshipsApi;

export default scholarshipsApi;
export { scholarshipsApi };
